import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { api } from '@api'

export interface ScrapedProfile {
  username: string | null
  fullName: string | null
  firstName: string | null
  lastName: string | null
  avatarUrl: string | null
  bio: string | null
  profileUrl: string | null
}

export interface SocialImportResultsResponse {
  items: ScrapedProfile[]
  total: number
  offset: number
  limit: number
  alreadyImportedUsernames: string[]
}

const fetchSocialImportResults = async (
  importId: string,
  offset: number,
  limit: number,
): Promise<SocialImportResultsResponse> => {
  const { data } = await api.get<SocialImportResultsResponse>(
    `/ft_import-contacts/${importId}/results`,
    { params: { offset, limit } },
  )
  return data
}

export const useFtGetSocialImportResults = (
  importId: string | null,
  offset: number = 0,
  limit: number = 50,
  enabled: boolean = true,
) => {
  return useQuery<SocialImportResultsResponse>({
    queryKey: ['ft_social_import_results', importId, offset, limit],
    queryFn: () => fetchSocialImportResults(importId!, offset, limit),
    enabled: !!importId && enabled,
  })
}

const RESULTS_PAGE_SIZE = 100

// Offset of the page after `last`, or undefined when there is none.
// - The corrected API gives the size of the whole import as `total`.
// - The API before it gave the size of the page: a full page then says nothing,
//   so one more page is asked for, and an empty page ends the list.
export const nextSocialImportResultsOffset = (
  last: Pick<SocialImportResultsResponse, 'items' | 'total' | 'offset' | 'limit'>,
): number | undefined => {
  if (last.items.length === 0) return undefined
  const moreAnnounced = last.offset + last.limit < last.total
  const fullPageOfOldApi = last.total === last.items.length && last.items.length === last.limit
  return moreAnnounced || fullPageOfOldApi ? last.offset + last.limit : undefined
}

// The size of an import to show: the number stored with the import can be 0 or
// too low (counted before the results were all there), so it never hides what
// the results themselves announce.
export const socialImportResultsTotal = (
  storedTotal: number | null | undefined,
  firstPageTotal: number | null | undefined,
): number => Math.max(storedTotal ?? 0, firstPageTotal ?? 0)

// The results of an import, 100 at a time (the API gives no more per request).
export const useFtGetSocialImportResultsPages = (importId: string | null, enabled: boolean = true) => {
  return useInfiniteQuery({
    queryKey: ['ft_social_import_results', importId, 'pages'],
    queryFn: ({ pageParam }) => fetchSocialImportResults(importId!, pageParam, RESULTS_PAGE_SIZE),
    initialPageParam: 0,
    getNextPageParam: nextSocialImportResultsOffset,
    enabled: !!importId && enabled,
    // Expired results (410) will not come back: say so at once instead of trying again.
    retry: (failureCount, error) =>
      (error as { response?: { status?: number } })?.response?.status !== 410 && failureCount < 3,
  })
}
